import axios, { InternalAxiosRequestConfig } from "axios";
import EventEmitter from "events";
import {
  IEncryptedData,
  decryptPayload,
  encryptPayload,
  isValidJSON,
} from "utils";
import { storage } from "utils/storage";

import { messagePartSeparator } from "ui/custom-toast/CustomToast";

import { API_BASE_URLS } from "./apiConfig";
import { errorReducer } from "./errorReducer";
import {
  API_ERROR_MESSAGES,
  IApiCallPayload,
  IApiErrorData,
  IApiErrorDetails,
} from "./types";

export const apiErrorEmitter = new EventEmitter();

const UNAUTHORIZED_CODE = 401;
const INTERNAL_SERVER_ERROR_CODE = 500;
const axiosInstance = axios.create();
const LOGOUT_REDIRECT_TIMER = 1500;

axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Add Authorization headers or other configurations here
    const authToken = storage.authToken;
    config.headers.Authorization = authToken;
    return config;
  },
  (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Handle errors globally (e.g., log out user on 401)
    if (error.status === UNAUTHORIZED_CODE) {
      const errorMessage = error?.response?.data?.message;
      if (errorMessage !== API_ERROR_MESSAGES.PERMISSION_ERROR) {
        storage.authToken = null;
        storage.userData = null;
        setTimeout(() => {
          window.location.reload();
        }, LOGOUT_REDIRECT_TIMER);
      }
    }
    const data = error.response?.data;
    const decryptPromise =
      data?.iv && data?.encryptedValue
        ? decryptPayload(data)
        : Promise.resolve(data);

    return decryptPromise.then((decryptedData) => {
      const errorDetails: IApiErrorDetails = {
        url: error.config?.url || "Unknown URL",
        data: decryptedData || "Unknown error",
        status: error.response?.status || INTERNAL_SERVER_ERROR_CODE,
      };
      apiErrorEmitter.emit("apiError", errorDetails);
      return Promise.reject(error);
    });
  }
);

export const apiCall = async <T extends object>(
  payload: IApiCallPayload
): Promise<T | IApiErrorData> => {
  const { apiRoute, module, config, useEncryption } = payload;
  const baseURL = API_BASE_URLS[module];
  try {
    let requestData = config.data;
    if (requestData && useEncryption) {
      requestData = encryptPayload(requestData);
    }
    const response = await axiosInstance.request<T>({
      ...config,
      baseURL,
      url: apiRoute,
      ...(requestData
        ? {
            data: requestData,
          }
        : {}),
    });
    const { data } = response;

    let responseData = data as any;
    if (responseData?.iv && responseData?.encryptedValue) {
      const rawResponseData = await decryptPayload(data as IEncryptedData);
      responseData = isValidJSON(rawResponseData)
        ? JSON.parse(rawResponseData)
        : rawResponseData;
    }
    return responseData;
  } catch (error) {
    console.error("error: ", error);
    if (axios.isAxiosError(error)) {
      // Handle errors returned from the server
      if (error.response) {
        const statusCode = error.response.status;
        let message;
        const status = "failed";
        const err = true;

        if (error?.response.data && error?.response?.data?.validationErrors) {
          const errorMessages = errorReducer(error.response.data);
          message = errorMessages.join(messagePartSeparator);
        } else if (useEncryption) {
          const errorData = await decryptPayload(error.response.data);
          message = errorData.message;
        } else {
          message = error.response.data.message;
        }
        const errorMessage =
          error?.response?.data?.message || API_ERROR_MESSAGES.NOT_AUTHORIZED;
        return {
          status,
          statusCode,
          message: statusCode === UNAUTHORIZED_CODE ? errorMessage : message,
          error: err,
        };
      } else if (error.request) {
        return {
          status: "failed",
          statusCode: 503,
          message: API_ERROR_MESSAGES.NO_SERVER_RESPONSE,
          error: true,
        };
      }
    }
    // Catch unexpected errors
    return {
      status: "failed",
      statusCode: 500,
      message: API_ERROR_MESSAGES.INTERNAL_SERVER_ERROR,
      error: true,
    };
  }
};
